import React, { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import axios from 'axios'
import { Layout } from './components/Layout'
import { useAuthStore } from './stores/authStore'
import { useHealthStore } from './stores/healthStore'
import { OfflineBanner } from './components/OfflineBanner'
import { useBackendHealth } from './hooks/useBackendHealth'
import { initErrorTracking, captureException } from './lib/errorTracking'
import { SheetStackProvider } from '@/hooks/useSheetStack'
import { logger } from './lib/logger'

// Feed the health store from query results so consecutive fetch failures and
// 5xx responses count toward the offline threshold without an extra ping.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onSuccess: () => {
      useHealthStore.getState().recordSuccess()
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status
        if (!error.response || (status !== undefined && status >= 500)) {
          useHealthStore.getState().recordFailure()
        }
      } else if (error instanceof TypeError) {
        // Native fetch network error
        useHealthStore.getState().recordFailure()
      }
    },
  }),
})

// Initialize Sentry once at module load — safe to call with no DSN configured
initErrorTracking()

// Lazy-loaded pages — each page is code-split into its own chunk
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Customers = lazy(() => import('./pages/Customers'))
const QuoteBuilder = lazy(() => import('./pages/Quotes/QuoteBuilder'))
const Calendar = lazy(() => import('./pages/Calendar'))
const Inbox = lazy(() => import('./pages/Inbox'))
const Workflows = lazy(() => import('./pages/Workflows'))
const Marketing = lazy(() => import('./pages/Marketing'))
const Reports = lazy(() => import('./pages/Reports'))
const Settings = lazy(() => import('./pages/Settings'))
const Work = lazy(() => import('./pages/WorkUnifiedMockup'))
const PublicBooking = lazy(() => import('./pages/PublicBooking'))
const PublicBookingManage = lazy(() => import('./pages/PublicBookingManage'))
const PublicSmsTerms = lazy(() => import('./pages/PublicSmsTerms'))
const LegalPrivacy = lazy(() => import('./pages/LegalPrivacy'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Error boundary catches any render crash and shows it on screen instead of white
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error('[ErrorBoundary]', error)
    captureException(error, { componentStack: info.componentStack ?? undefined })
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: '#0f172a', color: '#f1f5f9', minHeight: '100vh', padding: '40px', fontFamily: 'sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ maxWidth: '480px', textAlign: 'center' }}>
            <h1 style={{ color: '#f87171', fontSize: '20px', marginBottom: '12px', fontWeight: 600 }}>
              Something went wrong
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.60)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
              An unexpected error occurred. Please refresh the page or contact support if the problem persists.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ background: '#FFFFFF', color: '#000', border: 'none', borderRadius: '9999px', padding: '10px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Refresh page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Redirect helper for entity deep links — detects entity type from path and adds prefix
function RedirectToWork() {
  const { id } = useParams()
  const location = useLocation()
  let prefix = ''
  if (location.pathname.startsWith('/quotes/')) prefix = 'q:'
  else if (location.pathname.startsWith('/jobs/')) prefix = 'job:'
  else if (location.pathname.startsWith('/invoices/')) prefix = 'inv:'
  else if (location.pathname.startsWith('/deals/')) prefix = 'deal:'
  return <Navigate to={`/work/${encodeURIComponent(prefix + id)}`} replace />
}

function RedirectToCustomersPerson() {
  const { id } = useParams()
  return <Navigate to={`/customers/people/${id}`} replace />
}

function RedirectToCustomersCompany() {
  const { id } = useParams()
  return <Navigate to={`/customers/companies/${id}`} replace />
}

// Loading fallback shown while lazy chunks are fetched
function PageLoader() {
  return (
    <div className="page-container">
      <div className="glass-panel p-8 text-center text-slate-400">Loading...</div>
    </div>
  )
}

// Full-page loading spinner shown during session restoration
function AppLoader() {
  return (
    <div className="min-h-screen bg-obsidian-950 flex items-center justify-center">
      <div className="aurora-bg" aria-hidden="true" />
      <div className="relative z-10 flex flex-col items-center gap-4 animate-fade-in">
        <svg className="w-8 h-8 animate-spin text-white/70" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm text-ink-400">Loading...</span>
      </div>
    </div>
  )
}

// Wrapper that redirects unauthenticated users to /login
// and forces password change when mustResetPassword is set.
// Uses per-field selectors so this component only re-renders when one of
// these three values changes — not on every authStore mutation.
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated  = useAuthStore((s) => s.isAuthenticated)
  const loading          = useAuthStore((s) => s.loading)
  const mustResetPassword = useAuthStore((s) => s.mustResetPassword)

  if (loading) return <AppLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (mustResetPassword) return <Navigate to="/change-password" replace />
  return <>{children}</>
}

// Mounts the backend-health poller. Rendered once near the app root.
function BackendHealthPoller() {
  useBackendHealth()
  return null
}

function AppRoutes() {
  const restoreSession = useAuthStore((s) => s.restoreSession)

  useEffect(() => {
    restoreSession().catch((err) => {
      // restoreSession only re-throws on network errors; don't force logout on a blip
      logger.warn('[App] Session restore network error — keeping current auth state:', err)
    })
  }, [restoreSession])

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public auth routes — no sidebar/topbar */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        {/* Tier 2 Wave 3A — public booking page (no auth) */}
        <Route path="/book/:workspaceSlug" element={<PublicBooking />} />
        {/* Tier 2 Wave 3C — customer cancel + reschedule pages. Order matters:
            literal /cancel and /reschedule must match before the catch-all
            :serviceSlug below. */}
        <Route path="/book/:workspaceSlug/cancel" element={<PublicBookingManage action="cancel" />} />
        <Route path="/book/:workspaceSlug/reschedule" element={<PublicBookingManage action="reschedule" />} />
        {/* A2P 10DLC — messaging disclosure submitted to TCR as opt-in evidence.
            Literal segment, so it must precede :serviceSlug for the same reason
            /cancel and /reschedule do. */}
        <Route path="/book/:workspaceSlug/sms-terms" element={<PublicSmsTerms />} />
        <Route path="/book/:workspaceSlug/:serviceSlug" element={<PublicBooking />} />
        {/* Public privacy policy — the campaign registration links here. */}
        <Route path="/legal/privacy" element={<LegalPrivacy />} />
        {/* Wave 3B fallback URLs (still in customer inboxes). */}
        <Route path="/booking/cancel" element={<PublicBookingManage action="cancel" />} />
        <Route path="/booking/reschedule" element={<PublicBookingManage action="reschedule" />} />

        {/* Protected app routes — wrapped in Layout with sidebar/topbar */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"    element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/customers/*"  element={<ProtectedRoute><Layout><Customers /></Layout></ProtectedRoute>} />
        <Route path="/contacts/:id" element={<ProtectedRoute><RedirectToCustomersPerson /></ProtectedRoute>} />
        <Route path="/contacts"     element={<Navigate to="/customers/people" replace />} />
        <Route path="/companies/:id" element={<ProtectedRoute><RedirectToCustomersCompany /></ProtectedRoute>} />
        <Route path="/companies"    element={<Navigate to="/customers/companies" replace />} />
        <Route path="/work/:id?"    element={<ProtectedRoute><Layout><Work /></Layout></ProtectedRoute>} />
        <Route path="/quotes/builder" element={<ProtectedRoute><Layout><QuoteBuilder /></Layout></ProtectedRoute>} />
        <Route path="/quotes/new"   element={<Navigate to="/quotes/builder" replace />} />
        <Route path="/quotes/:id"   element={<ProtectedRoute><RedirectToWork /></ProtectedRoute>} />
        <Route path="/quotes"       element={<Navigate to="/work" replace />} />
        <Route path="/jobs/new"     element={<Navigate to="/work" replace />} />
        <Route path="/jobs/:id"     element={<ProtectedRoute><RedirectToWork /></ProtectedRoute>} />
        <Route path="/jobs"         element={<Navigate to="/work" replace />} />
        <Route path="/deals/:id"    element={<ProtectedRoute><RedirectToWork /></ProtectedRoute>} />
        <Route path="/deals"        element={<Navigate to="/work" replace />} />
        <Route path="/pipeline"     element={<Navigate to="/work" replace />} />
        <Route path="/pipeline/:id" element={<Navigate to="/work" replace />} />
        <Route path="/calendar"     element={<ProtectedRoute><Layout><Calendar /></Layout></ProtectedRoute>} />
        <Route path="/invoices/:id" element={<ProtectedRoute><RedirectToWork /></ProtectedRoute>} />
        <Route path="/invoices"     element={<Navigate to="/work" replace />} />
        <Route path="/inbox"        element={<ProtectedRoute><Layout><Inbox /></Layout></ProtectedRoute>} />
        <Route path="/workflows/*"  element={<ProtectedRoute><Layout><Workflows /></Layout></ProtectedRoute>} />
        <Route path="/marketing/*"  element={<ProtectedRoute><Layout><Marketing /></Layout></ProtectedRoute>} />
        <Route path="/social-media" element={<Navigate to="/marketing/social" replace />} />
        <Route path="/reviews"      element={<Navigate to="/marketing/reviews" replace />} />
        <Route path="/reports"      element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
        <Route path="/settings/*"   element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
        <Route path="*"             element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <SheetStackProvider>
          <Router>
            <BackendHealthPoller />
            <OfflineBanner />
            <AppRoutes />
          </Router>
        </SheetStackProvider>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
